/* ============================================
   Apps → Planes
   Catálogo de planes de crédito + quién los tiene contratado.

   Un plan es de la EMPRESA: le acredita una cuota mensual de créditos que se
   consume con todas sus apps. La contratación la hace el cliente desde
   /portal/suscripcion (checkout de Mercado Pago); acá solo se define la oferta.
   ============================================ */

import { useState, useEffect, useCallback, type FormEvent } from 'react'
import { useApi } from '@/hooks/useApi'
import Modal from '@/components/layout/Modal'
import { showToast } from '@/components/layout/Toast'
import { IconPlus, IconEdit, IconTrash, IconAlertCircle } from '@/components/icons/Icons'

interface Plan {
  id: number
  slug: string
  nombre: string
  descripcion: string | null
  precio: string
  moneda: string
  frecuencia: string
  creditos_incluidos: number
  activo: boolean
  orden: number
}

interface Suscripcion {
  id: number
  cliente_id: number
  cliente_nombre: string | null
  plan_nombre: string | null
  estado: string
  monto: string
  moneda: string
  creditos_incluidos: number
  proximo_cobro: string | null
  ultimo_cobro_at: string | null
  ultimo_error: string | null
}

interface Form {
  slug: string; nombre: string; descripcion: string
  precio: string; moneda: string; frecuencia: string
  creditos_incluidos: string; activo: boolean; orden: string
}

const vacio: Form = {
  slug: '', nombre: '', descripcion: '', precio: '', moneda: 'PEN',
  frecuencia: 'mensual', creditos_incluidos: '', activo: false, orden: '0',
}

function slugify(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
}

/** Costo por crédito de un plan y descuento contra el crédito suelto. */
function porCredito(p: Plan): number { return Number(p.precio) / p.creditos_incluidos }
function fmt4(v: number): string { return v.toFixed(4) }

const ESTADO_BADGE: Record<string, string> = {
  activa: 'badge-activo', pendiente: 'badge-pausado', impaga: 'badge-rechazada',
  pausada: 'badge-pausado', cancelada: 'badge-cancelado',
}

function fecha(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })
}

export default function AppsPlanes() {
  const api = useApi()
  const [planes, setPlanes] = useState<Plan[]>([])
  const [subs, setSubs] = useState<Suscripcion[]>([])
  const [pendientes, setPendientes] = useState(0)
  const [suelto, setSuelto] = useState<number | null>(null)
  const [sueltoIn, setSueltoIn] = useState('')
  const [cargando, setCargando] = useState(true)

  const [modal, setModal] = useState(false)
  const [edit, setEdit] = useState<Plan | null>(null)
  const [form, setForm] = useState<Form>(vacio)
  const [guardando, setGuardando] = useState(false)
  const [borrar, setBorrar] = useState<Plan | null>(null)

  const cargar = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        api.get<{ items: Plan[]; precio_credito_adicional: number | null; moneda_creditos: string }>('/api/planes')
          .catch(() => ({ items: [], precio_credito_adicional: null, moneda_creditos: 'PEN' })),
        api.get<{ items: Suscripcion[]; creditos_pendientes: number }>('/api/planes/suscripciones')
          .catch(() => ({ items: [], creditos_pendientes: 0 })),
      ])
      setPlanes(p?.items ?? [])
      setSuelto(p?.precio_credito_adicional ?? null)
      setSueltoIn(p?.precio_credito_adicional != null ? String(p.precio_credito_adicional) : '')
      setSubs(s?.items ?? [])
      setPendientes(s?.creditos_pendientes ?? 0)
    } finally {
      setCargando(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function guardarSuelto() {
    const v = Number(sueltoIn)
    if (!(v > 0)) { showToast('El precio debe ser mayor a 0', 'error'); return }
    try {
      await api.put('/api/planes/config-creditos', { precio_credito_adicional: v })
      showToast('Precio de referencia actualizado', 'success')
      await cargar()
    } catch (err: any) {
      showToast(err?.message || 'No se pudo guardar', 'error')
    }
  }

  useEffect(() => { cargar() }, [cargar])

  const abrirNuevo = () => { setEdit(null); setForm({ ...vacio }); setModal(true) }
  const abrirEdit = (p: Plan) => {
    setEdit(p)
    setForm({
      slug: p.slug, nombre: p.nombre, descripcion: p.descripcion ?? '',
      precio: String(Number(p.precio)), moneda: p.moneda, frecuencia: p.frecuencia,
      creditos_incluidos: String(p.creditos_incluidos), activo: p.activo, orden: String(p.orden),
    })
    setModal(true)
  }

  async function guardar(e: FormEvent) {
    e.preventDefault()
    setGuardando(true)
    const cuerpo = {
      slug: form.slug.trim() || slugify(form.nombre),
      nombre: form.nombre.trim(),
      descripcion: form.descripcion.trim() || null,
      precio: Number(form.precio),
      moneda: form.moneda,
      frecuencia: form.frecuencia,
      creditos_incluidos: Number(form.creditos_incluidos),
      activo: form.activo,
      orden: Number(form.orden) || 0,
    }
    try {
      if (edit) await api.put(`/api/planes/${edit.id}`, cuerpo)
      else await api.post('/api/planes', cuerpo)
      showToast(edit ? 'Plan actualizado' : 'Plan creado', 'success')
      setModal(false)
      await cargar()
    } catch (err: any) {
      showToast(err?.message || 'No se pudo guardar el plan', 'error')
    } finally {
      setGuardando(false)
    }
  }

  async function eliminar() {
    if (!borrar) return
    try {
      const r = await api.del<{ desactivado?: boolean }>(`/api/planes/${borrar.id}`)
      showToast(r?.desactivado ? 'El plan tiene contrataciones: se desactivó' : 'Plan eliminado', 'success')
      setBorrar(null)
      await cargar()
    } catch (err: any) {
      showToast(err?.message || 'No se pudo eliminar', 'error')
    }
  }

  if (cargando) return <div style={{ padding: 24, color: 'var(--text-muted)' }}>Cargando planes…</div>

  return (
    <>
      {pendientes > 0 && (
        <div className="card" style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
          <IconAlertCircle size={16} />
          <span>
            Hay <strong>{pendientes}</strong> cobro{pendientes === 1 ? '' : 's'} cobrado{pendientes === 1 ? '' : 's'} cuya
            cuota de créditos todavía no se acreditó en el gateway (falta que publique el endpoint de recarga).
            Se reponen con el botón de reintento una vez que exista.
          </span>
        </div>
      )}

      {/* Ancla de precio: el crédito suelto. Cada plan se compara contra esto,
          así que subirlo o bajarlo mueve el descuento aparente de TODOS los planes. */}
      <div className="card" style={{ marginBottom: 16 }}>
        <label className="form-label">Precio del crédito suelto (sin plan)</label>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input className="form-input" type="number" step="0.0001" min="0" value={sueltoIn}
            onChange={e => setSueltoIn(e.target.value)} style={{ maxWidth: 160 }} />
          <button className="btn btn-secondary" onClick={guardarSuelto}>Guardar</button>
          <span style={{ color: 'var(--text-muted)', fontSize: 12.5 }}>
            Es la referencia contra la que el portal calcula el ahorro de cada plan.
            Cambiarlo mueve el descuento que ve el cliente en todos los planes a la vez.
          </span>
        </div>
      </div>

      <div className="toolbar" style={{ marginBottom: 14 }}>
        <button className="btn btn-primary" onClick={abrirNuevo}><IconPlus size={15} /> Nuevo plan</button>
      </div>

      <div className="table-container" style={{ marginBottom: 28 }}>
        <table className="table">
          <thead>
            <tr>
              <th>Plan</th><th>Precio</th><th>Créditos</th><th>Por crédito</th>
              <th>Frecuencia</th><th>Estado</th><th></th>
            </tr>
          </thead>
          <tbody>
            {planes.length === 0 ? (
              <tr><td colSpan={7} style={{ color: 'var(--text-muted)' }}>Todavía no hay planes.</td></tr>
            ) : planes.map(p => (
              <tr key={p.id}>
                <td>
                  <strong>{p.nombre}</strong>
                  {p.descripcion && <div style={{ color: 'var(--text-muted)', fontSize: 12.5 }}>{p.descripcion}</div>}
                </td>
                <td>{p.moneda} {Number(p.precio).toFixed(2)}</td>
                <td>{p.creditos_incluidos.toLocaleString('es-PE')}</td>
                <td>
                  {p.moneda} {fmt4(porCredito(p))}
                  {suelto != null && (
                    <span style={{ color: 'var(--text-muted)', fontSize: 12, marginLeft: 6 }}>
                      {porCredito(p) < suelto
                        ? `−${Math.round((1 - porCredito(p) / suelto) * 100)}%`
                        : '⚠ no ahorra'}
                    </span>
                  )}
                </td>
                <td>{p.frecuencia}</td>
                <td>
                  <span className={`badge ${p.activo ? 'badge-activo' : 'badge-borrador'}`}>
                    {p.activo ? 'Publicado' : 'Borrador'}
                  </span>
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => abrirEdit(p)}><IconEdit size={14} /></button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setBorrar(p)}><IconTrash size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Clientes suscritos</h3>
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Cliente</th><th>Plan</th><th>Importe</th>
              <th>Créditos/mes</th><th>Próximo cobro</th><th>Último cobro</th><th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {subs.length === 0 ? (
              <tr><td colSpan={7} style={{ color: 'var(--text-muted)' }}>Nadie contrató un plan todavía.</td></tr>
            ) : subs.map(s => (
              <tr key={s.id}>
                <td><strong>{s.cliente_nombre || `#${s.cliente_id}`}</strong></td>
                <td>{s.plan_nombre ?? '—'}</td>
                <td>{s.moneda} {Number(s.monto).toFixed(2)}</td>
                <td>{s.creditos_incluidos.toLocaleString('es-PE')}</td>
                <td>{fecha(s.proximo_cobro)}</td>
                <td>{fecha(s.ultimo_cobro_at)}</td>
                <td>
                  <span className={`badge ${ESTADO_BADGE[s.estado] ?? 'badge-borrador'}`}>{s.estado}</span>
                  {s.ultimo_error && <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{s.ultimo_error}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal isOpen={modal} onClose={() => setModal(false)} title={edit ? 'Editar plan' : 'Nuevo plan'}>
        <form onSubmit={guardar}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Nombre *</label>
              <input className="form-input" value={form.nombre} required autoFocus
                onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Slug</label>
              <input className="form-input" value={form.slug} placeholder={slugify(form.nombre) || 'auto'}
                onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Descripción</label>
            <textarea className="form-textarea" rows={2} value={form.descripcion}
              placeholder="Una línea: para quién es este plan."
              onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Precio *</label>
              <input className="form-input" type="number" step="0.01" min="0" required value={form.precio}
                onChange={e => setForm(f => ({ ...f, precio: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Moneda</label>
              <select className="form-input" value={form.moneda}
                onChange={e => setForm(f => ({ ...f, moneda: e.target.value }))}>
                <option value="PEN">PEN</option>
                <option value="USD">USD</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Frecuencia</label>
              <select className="form-input" value={form.frecuencia}
                onChange={e => setForm(f => ({ ...f, frecuencia: e.target.value }))}>
                <option value="mensual">Mensual</option>
                <option value="anual">Anual</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Créditos incluidos *</label>
              <input className="form-input" type="number" min="1" required value={form.creditos_incluidos}
                onChange={e => setForm(f => ({ ...f, creditos_incluidos: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Orden</label>
              <input className="form-input" type="number" value={form.orden}
                onChange={e => setForm(f => ({ ...f, orden: e.target.value }))} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={form.activo}
                onChange={e => setForm(f => ({ ...f, activo: e.target.checked }))} />
              Publicado (visible para los clientes en el portal)
            </label>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={guardando}>
              {guardando ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={!!borrar} onClose={() => setBorrar(null)} title="Eliminar plan">
        <p>¿Eliminar el plan <strong>{borrar?.nombre}</strong>?</p>
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
          Si algún cliente lo tiene contratado no se borra: se despublica, para no romper
          el histórico de sus facturas.
        </p>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={() => setBorrar(null)}>Cancelar</button>
          <button className="btn btn-danger" onClick={eliminar}>Eliminar</button>
        </div>
      </Modal>
    </>
  )
}
